import { useCallback } from 'react';
import { FormattedMessage } from 'react-intl';
import { useNavigate } from 'react-router-dom-v5-compat';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBolt } from '@fortawesome/pro-solid-svg-icons/faBolt';
import { faBarcode } from '@fortawesome/pro-solid-svg-icons/faBarcode';
import { faPlus } from '@fortawesome/pro-solid-svg-icons/faPlus';

import { Button } from 'ui-components/components/button';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';
import { FlexColumn, FlexRow, Gap } from 'ui-components/components/flex';
import { Text, TextType } from 'ui-components/components/text';

import { messages as uomMessages } from '../../messages';
import { uomUpsellEmptyStateMessages } from './uom-upsell-empty-state.messages';
import styles from './uom-upsell-empty-state.module.less';

export const UomUpsellEmptyState = () => {
  const navigate = useNavigate();

  const handleUpgrade = useCallback(() => {
    navigate('/settings/payments');
  }, [navigate]);

  return (
    <div className={styles['upsell-container']}>
      <FlexRow className={styles['banner']} gap={Gap.Space16}>
        <FontAwesomeIcon className={styles['banner-icon']} icon={faBolt} size='lg' />
        <FlexColumn className={styles['banner-text']}>
          <Text
            className={styles['banner-title']}
            element='div'
            textType={TextType.Paragraph}
            value={uomUpsellEmptyStateMessages.bannerTitle}
          />
          <Text
            className={styles['banner-subtitle']}
            element='div'
            textType={TextType.Caption}
            value={uomUpsellEmptyStateMessages.bannerSubtitle}
          />
        </FlexColumn>
        <Button onClick={handleUpgrade} theme={THEME_CONSTANTS.promotional}>
          <FormattedMessage {...uomUpsellEmptyStateMessages.upgradeButton} />
        </Button>
      </FlexRow>

      <div className={styles['disabled-preview']}>
        <div className={styles['preview-card']}>
          <FlexRow className={styles['preview-header']}>
            <div>
              <Text element='div' textType={TextType.H4} value={uomMessages.uomConfigurationTitle} />
              <Text
                className={styles['preview-subtitle']}
                element='div'
                textType={TextType.Caption}
                value={uomMessages.uomConfigurationSubtitle}
              />
            </div>
            <Button className={styles['print-btn']} disabled theme={THEME_CONSTANTS.secondary}>
              <FontAwesomeIcon icon={faBarcode} />
              <FormattedMessage {...uomMessages.uomPrintBarcodes} />
            </Button>
          </FlexRow>

          <div className={styles['grid']}>
            <div className={styles['grid-header']}>
              <Text element='div' textType={TextType.Label} value={uomMessages.uomHeaderType} />
              <Text element='div' textType={TextType.Label} value={uomMessages.uomHeaderQuantity} />
              <Text element='div' textType={TextType.Label} value={uomMessages.uomHeaderBarcode} />
              <Text element='div' textType={TextType.Label} value={uomMessages.uomHeaderUomSku} />
              <Text element='div' textType={TextType.Label} value={uomMessages.uomHeaderDimensions} />
              <Text element='div' textType={TextType.Label} value={uomMessages.uomHeaderWeight} />
              <Text element='div' textType={TextType.Label} value={uomMessages.uomHeaderStackable} />
            </div>
            <div className={styles['grid-row']}>
              <Text element='span' textType={TextType.Caption} value='Each' />
              <Text element='span' textType={TextType.Caption} value='1' />
              <Text element='span' textType={TextType.Caption} value='lift-tank' />
              <Text element='span' textType={TextType.Caption} value='lift-tank' />
              <Text className={styles['grid-cell-dash']} element='span' textType={TextType.Caption} value={'—'} />
              <Text className={styles['grid-cell-dash']} element='span' textType={TextType.Caption} value={'—'} />
              <Text element='span' textType={TextType.Caption} value='Yes' />
            </div>
            <div className={styles['grid-row']}>
              <Text element='span' textType={TextType.Caption} value='Pack' />
              <Text element='span' textType={TextType.Caption} value='10 x Each' />
              <Text element='span' textType={TextType.Caption} value='049383271001' />
              <Text element='span' textType={TextType.Caption} value='lift-tank-pk' />
              <Text element='span' textType={TextType.Caption} value='8 x 6 x 4 (in)' />
              <Text element='span' textType={TextType.Caption} value='2lb 3oz' />
              <Text element='span' textType={TextType.Caption} value='Yes' />
            </div>
            <div className={styles['grid-row']}>
              <Text element='span' textType={TextType.Caption} value='Case' />
              <Text element='span' textType={TextType.Caption} value='5 x Pack' />
              <Text element='span' textType={TextType.Caption} value='049383271002' />
              <Text element='span' textType={TextType.Caption} value='lift-tank-cs' />
              <Text element='span' textType={TextType.Caption} value='18 x 12 x 10 (in)' />
              <Text element='span' textType={TextType.Caption} value='12lb 4oz' />
              <Text element='span' textType={TextType.Caption} value='Yes' />
            </div>
            <div className={styles['grid-row']}>
              <Text element='span' textType={TextType.Caption} value='Pallet' />
              <Text element='span' textType={TextType.Caption} value='10 x Case' />
              <Text element='span' textType={TextType.Caption} value='049383271003' />
              <Text element='span' textType={TextType.Caption} value='lift-tank-pl' />
              <Text element='span' textType={TextType.Caption} value='48 x 40 x 52 (in)' />
              <Text element='span' textType={TextType.Caption} value='128lb' />
              <Text element='span' textType={TextType.Caption} value='No' />
            </div>
          </div>

          <div className={styles['add-unit-row']}>
            <FontAwesomeIcon className={styles['add-unit-icon']} icon={faPlus} size='xs' />
            <Text element='span' textType={TextType.Paragraph} value={uomMessages.uomAddUnit} />
          </div>

          <div className={styles['conversion-summary']}>
            <Text
              className={styles['conversion-title']}
              element='div'
              textType={TextType.Label}
              value={uomMessages.uomConversionSummaryTitle}
            />
            <ul className={styles['conversion-list']}>
              <li className={styles['conversion-item']}>
                <Text element='span' textType={TextType.Caption} value={uomUpsellEmptyStateMessages.sampleConversionLine1} />
              </li>
              <li className={styles['conversion-item']}>
                <Text element='span' textType={TextType.Caption} value={uomUpsellEmptyStateMessages.sampleConversionLine2} />
              </li>
              <li className={styles['conversion-item']}>
                <Text element='span' textType={TextType.Caption} value={uomUpsellEmptyStateMessages.sampleConversionLine3} />
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};
